/* eslint-disable react/jsx-boolean-value */
import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { currentUserState } from '../utils/state-atoms'
import { isDashboardOnlyRole, isPathAllowedForDashboardOnly } from '../utils/roles'
import routes from './routes'

type Props = {
  element: ReactElement
}

const RequireAuth = ({ element }: Props) => {
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()
  const currentUser = useRecoilValue(currentUserState)

  const currentPath = location.pathname + location.search
  const existingRedirect = localStorage.getItem('postLoginRedirect')

  // Check if user needs to be redirected to login
  if ((inProgress === InteractionStatus.None && !isAuthenticated) || !currentUser) {
    // Save redirect if there's no existing one saved AND we're not on the login page
    // The key: only skip saving if there's ALREADY a redirect (prevents overwriting)
    // We allow saving even during 'startup' because that happens on initial page load
    if (!existingRedirect && currentPath !== routes.Login.absolutePath) {
      localStorage.setItem('postLoginRedirect', currentPath)
    }
    return <Navigate to={routes.Login.absolutePath} state={{ ref: currentPath }} replace={true} />
  } else {
    // Dashboard-only users are confined to dashboard and MAV areas; send any other
    // navigation back to the dashboard.
    if (
      isDashboardOnlyRole(currentUser.role?.roleId) &&
      !isPathAllowedForDashboardOnly(location.pathname)
    ) {
      return <Navigate to={routes.Dashboard.absolutePath} replace={true} />
    }
    return element
  }
}

export default RequireAuth
